/**
 * Weave GUI protocol v1 — the interaction REST client (contract §5.2, §6).
 *
 * Two endpoints and four typed failures. The failures are the reason this is a module rather than two
 * `fetch` calls: every one of them means something specific to a person looking at a card, and
 * collapsing them into "request failed" throws away the only information that tells the user what to
 * do next.
 *
 *   404 "interactions are not enabled"  the flag is off. NOT AN ERROR — the feature is absent, and a
 *                                       banner saying so is a lie about a working system (§6, and
 *                                       §10 of the frontend rules on copy that claims what it cannot
 *                                       know). Narrowed by `asInteractionsUnavailable`.
 *   409 EVIDENCE_NOT_READY              `approve_changeset` only, non-rejections only, checked BEFORE
 *                                       resolution — so the question is STILL PENDING afterwards and
 *                                       the card must stay on screen. `override_reason` is the one
 *                                       way past it, and it must be its own labelled affordance.
 *   409 INTERACTION_ALREADY_RESOLVED    somebody answered it in another tab. Carries `resume_from`.
 *   409 INTERACTION_EXPIRED             the TTL swept it. Carries `resume_from`, which is what makes
 *                                       it answerable by starting a NEW run rather than a dead end.
 *
 * Shapes are imported from the vendored protocol types rather than re-declared, so a pin bump
 * surfaces as a type error at the call site instead of a silent field mismatch (§11).
 *
 * Same construction as `changesets.ts` — endpoint builder, one error class carrying the parsed body,
 * `asXxx` narrowers — deliberately, so there is one way to read a Weave REST failure and not two.
 */

import type { Interaction, InteractionRespondBody, ResumeFrom } from "./protocol";


/**
 * The server's own cap on `override_reason`, and it is the SMALLER of two documented numbers.
 *
 * The contract's §12 schema says 2000; the handoff's A4 table, written off `interactions.py`, says
 * 1000. They disagree, and the asymmetry decides it: capping at the deployed 1000 costs a user some
 * characters, while allowing 2000 against a server that rejects it costs them their answer to a 422
 * they cannot act on. Raise this the day the contract and the code agree.
 */
export const OVERRIDE_REASON_MAX = 1000;

/** The server's cap on `free_text` (§5.2 / A4 agree at 8000). */
export const FREE_TEXT_MAX = 8000;

/**
 * `POST …/respond` body, plus the L-28 evidence-override binding.
 *
 * `override_unmet` NAMES the evidence ids `override_reason` waives — the exact list the card was
 * showing when it opened the override affordance (`InteractionCard`'s `gated` bit, computed from
 * `unmetEvidence`). Sent alongside a non-blank `override_reason`, it is what turns that reason from
 * a free-text field into a BOUND consent act (`services.override_consent`, backend). Omitting it, or
 * sending a set the gate no longer recognises, is refused typed — see `asOverrideNotBound` /
 * `asOverrideStale` below — never a silent 200.
 */
export type RespondBody = InteractionRespondBody & {
  override_unmet?: string[] | null;
};

/** `GET /api/runs/{id}/interactions`. `pending` prefers a BLOCKING interaction when one exists. */
export interface InteractionListOut {
  run_id: string;
  /** The id the UI should lead with, or `null`. Not necessarily the only pending one. */
  pending: string | null;
  interactions: Interaction[];
}

/**
 * `POST …/respond`. The three per-kind extras are optional because they are per-kind — a `clarify`
 * response carries none of them.
 */
export interface InteractionRespondOut {
  ok: boolean;
  interaction_id: string;
  /** The run's status AFTER the resolution. */
  run_status: string;
  /**
   * Whether the answer resumed a live run.
   *
   * `false` IS NOT A FAILURE (§B2): an advisory question answered after its run finished, or a claim
   * lost to another tab, both report `resumed: false` with no error. Surfacing it as one is how a
   * correct flow gets reported to the user as broken.
   */
  resumed: boolean;
  /** Present on a non-parked `approve_plan` — the handle for the execute run (§7i). */
  resume_from?: ResumeFrom | null;
  /** Present on `approve_changeset`. */
  apply_log_id?: string | null;
  changeset_id?: string | null;
  /** Present on `approve_budget` — the raised ceiling. */
  max_usd?: number | null;
}

/** Non-2xx from either endpoint. `payload` is the parsed body; a consumed body cannot be re-read. */
export class InteractionApiError extends Error {
  readonly status: number;
  readonly payload: unknown;
  constructor(status: number, message: string, payload: unknown) {
    super(message);
    this.name = "InteractionApiError";
    this.status = status;
    this.payload = payload;
  }
}

/** `409 EVIDENCE_NOT_READY` — the question survives; the card stays. */
export interface EvidenceNotReady {
  code: "EVIDENCE_NOT_READY";
  message: string;
  /** The blocking evidence items that are not `ready`. May be empty if the server sent none. */
  unmet: { evidence_id: string; kind: string; subject: string | null }[];
}

/** `409 INTERACTION_ALREADY_RESOLVED` or `INTERACTION_EXPIRED`. */
export interface InteractionClosed {
  code: "INTERACTION_ALREADY_RESOLVED" | "INTERACTION_EXPIRED";
  message: string;
  /** Set on expiry, and on some already-resolved bodies — the handle for a fresh run. */
  resume_from: ResumeFrom | null;
}

/**
 * `422 OVERRIDE_NOT_BOUND` — L-28. The override named nothing it waives (no `override_unmet`, or the
 * package it would cover could not be identified). Re-sending the SAME body will never work; the
 * card must gather `override_unmet` from what it is currently showing and resend.
 */
export interface OverrideNotBound {
  code: "OVERRIDE_NOT_BOUND";
  message: string;
  /** The live unmet-evidence ids the server wanted named — carried so the card can redraw the list
   *  it opened the override affordance for, rather than going blank. */
  unmet: string[];
}

/**
 * `409 OVERRIDE_STALE` — L-28. `override_unmet` named a set that no longer matches the gate's live
 * obligations: they moved between when the card rendered and when it was clicked. Sibling of
 * `EVIDENCE_NOT_READY` — the request was well-formed, the world moved — so the recovery is the same
 * shape: re-read the run and let the user retry against the current set.
 */
export interface OverrideStale {
  code: "OVERRIDE_STALE";
  message: string;
  /** The live unmet-evidence ids, as of this refusal. */
  unmet: string[];
}

/** The `detail` envelope FastAPI wraps a typed body in, unwrapped once. */
function detailOf(payload: unknown): Record<string, unknown> {
  const top = (payload ?? {}) as Record<string, unknown>;
  const detail = top.detail;
  return detail && typeof detail === "object" ? (detail as Record<string, unknown>) : top;
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * True when the 404 means "this deployment has interactions switched off" rather than
 * "no such run" or "no such interaction".
 *
 * Distinguished on the server's own sentence, because all three are 404 (§6). Getting this wrong in
 * either direction is a visible bug: read as unavailable, a genuine missing run silently renders an
 * empty tray; read as an error, a flag-off deployment shows a failure banner on every load.
 */
export function asInteractionsUnavailable(err: unknown): boolean {
  if (!(err instanceof InteractionApiError) || err.status !== 404) return false;
  const d = detailOf(err.payload);
  const text = `${str(d.detail) ?? ""} ${str(d.message) ?? ""} ${typeof err.payload === "string" ? err.payload : ""}`;
  return /not enabled|disabled/i.test(text);
}

/** Narrow to the evidence gate, or null. */
export function asEvidenceNotReady(err: unknown): EvidenceNotReady | null {
  if (!(err instanceof InteractionApiError) || err.status !== 409) return null;
  const d = detailOf(err.payload);
  if (str(d.code) !== "EVIDENCE_NOT_READY") return null;
  const rows = Array.isArray(d.unmet) ? d.unmet : [];
  return {
    code: "EVIDENCE_NOT_READY",
    message:
      str(d.message) ??
      "Some required checks have not finished yet, so this cannot be approved.",
    unmet: rows.map((row) => {
      const item = (row ?? {}) as Record<string, unknown>;
      const subject = item.subject;
      return {
        evidence_id: str(item.evidence_id) ?? "",
        kind: str(item.kind) ?? "unknown",
        subject:
          subject && typeof subject === "object"
            ? (str((subject as Record<string, unknown>).id) ?? null)
            : str(subject),
      };
    }),
  };
}

/** Narrow to "this question is closed", or null. */
export function asInteractionClosed(err: unknown): InteractionClosed | null {
  if (!(err instanceof InteractionApiError) || err.status !== 409) return null;
  const d = detailOf(err.payload);
  const code = str(d.code);
  if (code !== "INTERACTION_ALREADY_RESOLVED" && code !== "INTERACTION_EXPIRED") return null;
  const resume = d.resume_from;
  return {
    code,
    message:
      str(d.message) ??
      (code === "INTERACTION_EXPIRED"
        ? "This question expired before it was answered."
        : "This question was already answered."),
    resume_from: resume && typeof resume === "object" ? (resume as ResumeFrom) : null,
  };
}

/** Reads `detail.unmet` as a string array, tolerating anything else the server might send. */
function unmetIds(d: Record<string, unknown>): string[] {
  const rows = Array.isArray(d.unmet) ? d.unmet : [];
  return rows.filter((row): row is string => typeof row === "string");
}

/** Narrow to `422 OVERRIDE_NOT_BOUND`, or null. */
export function asOverrideNotBound(err: unknown): OverrideNotBound | null {
  if (!(err instanceof InteractionApiError) || err.status !== 422) return null;
  const d = detailOf(err.payload);
  if (str(d.code) !== "OVERRIDE_NOT_BOUND") return null;
  return {
    code: "OVERRIDE_NOT_BOUND",
    message:
      str(d.message) ??
      "This override needs to name the checks it waives — re-read them and try again.",
    unmet: unmetIds(d),
  };
}

/** Narrow to `409 OVERRIDE_STALE`, or null. */
export function asOverrideStale(err: unknown): OverrideStale | null {
  if (!(err instanceof InteractionApiError) || err.status !== 409) return null;
  const d = detailOf(err.payload);
  if (str(d.code) !== "OVERRIDE_STALE") return null;
  return {
    code: "OVERRIDE_STALE",
    message:
      str(d.message) ??
      "The required checks changed since this was shown — re-read them and try again.",
    unmet: unmetIds(d),
  };
}
